import { Dialog, DialogContent, DialogTitle } from "./ui/Dialog";

/**
 * The suggested-fix popup, shared by the AI Audit and Persona Recommendations tabs.
 *
 * The audit passes only `guide` — a list of resolution steps — and gets the same dialog it always
 * had. A persona fix additionally carries its own title, the rationale for the fix, and sometimes a
 * JSON-LD or copy example, so those render as extra sections and the steps gain a heading to
 * separate them.
 *
 * Built on the shared Radix `Dialog` rather than a hand-rolled overlay: this is the persona tab's
 * primary interaction, on a card grid a keyboard user has to tab through, so it needs the focus
 * trap, focus restore, Escape and backdrop dismissal the primitive already provides.
 */
const ImplementationGuideModal = ({ guide, title, rationale, example, onClose }) => {
  // `guide` is a step list from the audit, a single string from older payloads, or an empty array
  // from a persona fix with no steps. Only the last of those is truthy while having nothing to
  // show, which is why this asks about length rather than trusting `!guide`.
  const hasSteps = Array.isArray(guide) ? guide.length > 0 : Boolean(guide);
  const hasExtras = Boolean(rationale || example);

  if (!hasSteps && !hasExtras) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" aria-describedby={undefined}>
        <header className="p-6 pr-16 border-b border-surface-container-high">
          <DialogTitle className="text-xl">{title || "Implementation Guide"}</DialogTitle>
        </header>
        <main className="p-8 overflow-y-auto space-y-6">
          {rationale && <p className="text-on-surface-variant italic">{rationale}</p>}
          {hasSteps && (
            <div>
              {hasExtras && (
                <p className="text-xs font-semibold text-on-surface-variant mb-2">
                  Implementation Steps
                </p>
              )}
              {Array.isArray(guide) ? (
                <ul className="list-decimal list-inside space-y-4 text-on-surface-variant">
                  {guide.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-on-surface-variant">{guide}</p>
              )}
            </div>
          )}
          {example && (
            <div>
              <p className="text-xs font-semibold text-on-surface-variant mb-2">Example</p>
              <pre className="text-xs bg-surface-container-low rounded-lg p-3 overflow-x-auto text-on-surface-variant border border-surface-container-high whitespace-pre-wrap">
                {example}
              </pre>
            </div>
          )}
        </main>
        <footer className="p-6 border-t border-surface-container-high bg-surface-container-low text-right rounded-b-3xl">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-semibold text-on-primary bg-gradient-to-br from-primary to-primary-container rounded-xl hover:opacity-90 transition-colors"
          >
            Close Guide
          </button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};

export default ImplementationGuideModal;
